/**
 * Notification Gate Service
 * 
 * Central gate for all client-side notifications. Checks:
 * 1. User category preferences (itinerary, expenses, safety, documents, collaboration)
 * 2. Channel preferences (push, email, inApp)
 * 3. Trip-level mute
 * 4. Quiet hours
 * 5. Dedupe (via notificationDedupe)
 * 6. Rate limits (via notificationRateLimit)
 */

import { useUserStore, type NotificationCategory, type NotificationChannel } from '@/state/userStore';
import { checkAndMark, isDuplicate, DEFAULT_COOLDOWNS } from './notificationDedupe';
import { checkRateLimit, recordSent, type NotificationPriority } from './notificationRateLimit';

export type NotificationRequest = {
  /** Category for preference checking */
  category: NotificationCategory;
  /** Channel being used */
  channel: Exclude<NotificationChannel, 'email'>; // Client only does push/inApp
  /** Trip ID for trip-level mute checking */
  tripId?: string;
  /** Priority level (high bypasses rate limits) */
  priority: NotificationPriority;
  /** Unique key for deduplication */
  dedupeKey: string;
  /** Cooldown period in milliseconds (0 = no dedupe) */
  cooldownMs?: number;
};

export type GateResult = {
  allowed: boolean;
  reason?: 'category_disabled' | 'channel_disabled' | 'trip_muted' | 'quiet_hours' | 'duplicate' | 'burst_suppressed' | 'rate_limited';
};

/**
 * Default channel preferences (all enabled)
 */
const DEFAULT_CHANNELS = {
  push: true,
  email: true,
  inApp: true,
};

/**
 * Default category preferences (all enabled)
 */
const DEFAULT_CATEGORIES = {
  itinerary: true,
  expenses: true,
  safety: true,
  documents: true,
  collaboration: true,
};

// Cross-feature burst suppression to reduce notification storms.
// Applies to medium/low priority only; high priority alerts bypass.
const BURST_COOLDOWN_MS: Record<'medium' | 'low', number> = {
  medium: 10 * 60 * 1000,
  low: 20 * 60 * 1000,
};

const buildBurstKey = (request: NotificationRequest): string | null => {
  if (request.priority === 'high') {
    return null;
  }
  const scope = request.tripId || 'global';
  return `burst:${request.category}:${scope}:${request.priority}`;
};

// Serialize sends per dedupe key so concurrent calls cannot pass checks simultaneously.
const dedupeSendQueue = new Map<string, Promise<void>>();

const runWithDedupeLock = async <T>(dedupeKey: string, task: () => Promise<T>): Promise<T> => {
  const previous = dedupeSendQueue.get(dedupeKey) ?? Promise.resolve();
  const releaseRef: { current: (() => void) | null } = { current: null };
  const current = new Promise<void>((resolve) => {
    releaseRef.current = () => {
      resolve();
    };
  });
  const tail = previous
    .catch(() => {
      // Keep queue alive on upstream failures.
    })
    .then(() => current);
  dedupeSendQueue.set(dedupeKey, tail);

  await previous.catch(() => {
    // Ignore previous task errors to avoid poisoning the queue.
  });

  try {
    return await task();
  } finally {
    releaseRef.current?.();
    releaseRef.current = null;
    if (dedupeSendQueue.get(dedupeKey) === tail) {
      dedupeSendQueue.delete(dedupeKey);
    }
  }
};

/**
 * Check if we're currently in quiet hours
 */
const isQuietHours = (quietStart?: number, quietEnd?: number): boolean => {
  if (quietStart === undefined || quietEnd === undefined) {
    return false;
  }
  
  const now = new Date();
  const currentHour = now.getHours();
  
  // Handle quiet hours that span midnight (e.g., 22:00 - 07:00)
  if (quietStart > quietEnd) {
    return currentHour >= quietStart || currentHour < quietEnd;
  }
  
  // Normal case (e.g., 23:00 - 06:00 would be quietStart < quietEnd)
  return currentHour >= quietStart && currentHour < quietEnd;
};

/**
 * Get user notification preferences from the store
 */
const getUserPreferences = () => {
  const state = useUserStore.getState();
  const profile = state.profile;
  const prefs = profile.notificationPreference;
  
  return {
    categories: {
      itinerary: prefs?.itinerary ?? DEFAULT_CATEGORIES.itinerary,
      expenses: prefs?.expenses ?? DEFAULT_CATEGORIES.expenses,
      safety: prefs?.safety ?? DEFAULT_CATEGORIES.safety,
      documents: prefs?.documents ?? DEFAULT_CATEGORIES.documents,
      collaboration: prefs?.collaboration ?? DEFAULT_CATEGORIES.collaboration,
    },
    channels: {
      push: prefs?.channels?.push ?? DEFAULT_CHANNELS.push,
      email: prefs?.channels?.email ?? DEFAULT_CHANNELS.email,
      inApp: prefs?.channels?.inApp ?? DEFAULT_CHANNELS.inApp,
    },
    quietHoursStart: prefs?.quietHoursStart,
    quietHoursEnd: prefs?.quietHoursEnd,
    mutedTrips: profile.mutedTrips ?? [],
  };
};

/**
 * Check if a notification should be sent (without marking as sent)
 * 
 * @param request - Notification request details
 * @returns GateResult with allowed status and reason if blocked
 */
export const shouldSendNotification = async (request: NotificationRequest): Promise<GateResult> => {
  const prefs = getUserPreferences();
  
  // 1. Check category preference
  if (!prefs.categories[request.category]) {
    return { allowed: false, reason: 'category_disabled' };
  }
  
  // 2. Check channel preference
  if (!prefs.channels[request.channel]) {
    return { allowed: false, reason: 'channel_disabled' };
  }
  
  // 3. Check trip-level mute (only for push, not inApp)
  if (request.tripId && request.channel === 'push' && prefs.mutedTrips.includes(request.tripId)) {
    return { allowed: false, reason: 'trip_muted' };
  }
  
  // 4. Check quiet hours (only for non-high priority)
  if (request.priority !== 'high' && isQuietHours(prefs.quietHoursStart, prefs.quietHoursEnd)) {
    return { allowed: false, reason: 'quiet_hours' };
  }
  
  // 5. Check dedupe (if cooldown specified)
  if (request.cooldownMs && request.cooldownMs > 0) {
    const duplicate = await isDuplicate(request.dedupeKey);
    if (duplicate) {
      return { allowed: false, reason: 'duplicate' };
    }
  }

  // 5b. Cross-feature burst suppression (same category+trip+priority)
  const burstKey = buildBurstKey(request);
  if (burstKey) {
    const burstDuplicate = await isDuplicate(burstKey);
    if (burstDuplicate) {
      return { allowed: false, reason: 'burst_suppressed' };
    }
  }
  
  // 6. Check rate limit
  const underRateLimit = await checkRateLimit(request.priority);
  if (!underRateLimit) {
    return { allowed: false, reason: 'rate_limited' };
  }
  
  return { allowed: true };
};

/**
 * Send a gated notification - checks all gates and sends if allowed
 * 
 * @param request - Notification request details
 * @param sendFn - Function to actually send the notification
 * @returns true if notification was sent, false if blocked
 */
export const sendGatedNotification = async (
  request: NotificationRequest,
  sendFn: () => Promise<void>
): Promise<boolean> => {
  return runWithDedupeLock(request.dedupeKey, async () => {
    const result = await shouldSendNotification(request);
    
    if (!result.allowed) {
      if (__DEV__) {
        console.log(`[NotificationGate] Blocked: ${result.reason} for ${request.dedupeKey}`);
      }
      return false;
    }
    
    try {
      // Send the notification
      await sendFn();
      
      // Record for dedupe (if cooldown specified)
      if (request.cooldownMs && request.cooldownMs > 0) {
        await checkAndMark(request.dedupeKey, request.cooldownMs);
      }

      const burstKey = buildBurstKey(request);
      if (burstKey) {
        const p = request.priority;
        if (p === 'medium' || p === 'low') {
          await checkAndMark(burstKey, BURST_COOLDOWN_MS[p]);
        }
      }
      
      // Record for rate limit
      await recordSent(request.priority);
      
      return true;
    } catch (error) {
      console.error('[NotificationGate] Error sending notification:', error);
      return false;
    }
  });
};

/**
 * Helper to create a notification request with common defaults
 */
export const createNotificationRequest = (
  params: {
    category: NotificationCategory;
    channel?: Exclude<NotificationChannel, 'email'>;
    tripId?: string;
    priority?: NotificationPriority;
    dedupeKey: string;
    cooldownMs?: number;
  }
): NotificationRequest => ({
  category: params.category,
  channel: params.channel ?? 'push',
  tripId: params.tripId,
  priority: params.priority ?? 'medium',
  dedupeKey: params.dedupeKey,
  cooldownMs: params.cooldownMs ?? 0,
});

/**
 * Quick check if a category is enabled (without full gate check)
 */
export const isCategoryEnabled = (category: NotificationCategory): boolean => {
  const prefs = getUserPreferences();
  return prefs.categories[category];
};

/**
 * Quick check if a channel is enabled (without full gate check)
 */
export const isChannelEnabled = (channel: NotificationChannel): boolean => {
  const prefs = getUserPreferences();
  return prefs.channels[channel];
};

/**
 * Quick check if a trip is muted
 */
export const isTripMuted = (tripId: string): boolean => {
  const prefs = getUserPreferences();
  return prefs.mutedTrips.includes(tripId);
};

/**
 * Check if currently in quiet hours
 */
export const isInQuietHours = (): boolean => {
  const prefs = getUserPreferences();
  return isQuietHours(prefs.quietHoursStart, prefs.quietHoursEnd);
};

// Re-export cooldown constants for convenience
export { DEFAULT_COOLDOWNS } from './notificationDedupe';
export { RATE_LIMITS } from './notificationRateLimit';
